import React, { useEffect, useMemo } from "react";
import _ from "lodash";
import { RouterState } from "react-router/lib/Router";
import { connect, MapStateToProps } from "react-redux";
import { CircularProgress } from "material-ui";
import type { IScope } from "reducers/scope";
import { IStore } from "reducers";
import { useOrderGridData } from "./hooks";
import ShipmentReviewContainer from "./shipment-review-container";
import { SHIPMENT_VIEW } from "./shipment-review-types";
import { TileViewContainer } from "./tile-view-container";
import GridViewContainer from "./grid-view-container";
import { SchemaRecord } from "records/schema";
import { ICartData } from "reducers/cart-grid-view";
import { fetchViewForSchema } from "actions/views";
import { WORKING_ORDER_ITEMS } from "constants/view-defs";
import { fetchCustomGridViews } from "actions/cart-grid-view";

interface IOwnProps {
    router: RouterState;
}

interface IStateProps {
    scope: IScope;
    cartData: ICartData;
    schemaIds: string[];
    schemas: { [key: string]: SchemaRecord };
    views: { [key: string]: any }; // Record<IViewDefinition>
    dataLoading: boolean;
}

interface IDispatchProps {
    onFetchView: (schema: string) => void;
    onLoadCustomViews: () => void;
}

const styles = {
    loadingStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
    },
};

export const ShipmentReviewRootContainer: React.FunctionComponent<IOwnProps & IStateProps & IDispatchProps> = (
    props,
) => {
    const { router, scope, schemas, schemaIds, dataLoading: isFetching, views } = props;
    const { onFetchView, onLoadCustomViews } = props;

    const schemaMap = useMemo(() => _.pick(schemas, schemaIds), [schemaIds, schemas]);
    const data = useOrderGridData({ ...props, schemaMap });

    const isEditable = React.useMemo(
        () =>
            !scope.brand?.settings?.volume_pricing_customization_scripts && // TODO: supported, disabled for consistency
            props.cartData?.company && // TODO: remove as a part of GA experience, added for consistency
            (!scope.brand.hasPreference("allow_bulk_orders") || !props.cartData?.company?.allow_bulk), // no bulk supported so far
        [props.cartData?.company, scope.brand],
    );

    const itemView = useMemo(() => _.find(views, { view_name: WORKING_ORDER_ITEMS })?.toJS(), [views]);

    useEffect(() => {
        onLoadCustomViews();
    }, [onLoadCustomViews]);

    useEffect(() => {
        const schema = _.find(schemaMap, { type: "order" })?._id;
        if (schema) {
            onFetchView(schema);
        }
    }, [schemaMap, onFetchView]);

    if (!data.orderData || isFetching) {
        return (
            <div style={styles.loadingStyle}>
                <CircularProgress />
            </div>
        );
    }

    switch (router.location.query?.view) {
        case SHIPMENT_VIEW.TILE:
            return (
                <TileViewContainer
                    orderData={data.orderData}
                    productSchema={data.productSchema}
                    itemView={itemView}
                    editable={isEditable}
                    scope={scope}
                />
            );
        case SHIPMENT_VIEW.GRID:
            return (
                <GridViewContainer
                    orderData={data.orderData}
                    itemView={itemView}
                    productSchema={data.productSchema}
                    orderSchema={data.orderSchema}
                    isFetching={isFetching}
                />
            );
        default:
            return (
                <ShipmentReviewContainer
                    orderData={data.orderData}
                    productSchema={data?.productSchema}
                    itemView={itemView}
                    editable={isEditable}
                />
            );
    }
};

const mapStateToProps: MapStateToProps<IStateProps, IOwnProps> = (state: IStore): IStateProps => ({
    scope: state.scope,
    schemaIds: state.schemas.result,
    schemas: state.entities.schemas,
    views: state.entities.views,
    cartData: state.cartGridView.data,
    dataLoading: state.schemas.isFetching || state.views.isFetching || !state.cartGridView.customViews,
});

const mapDispatchToProps = (dispatch): IDispatchProps => ({
    onFetchView: (schemaId) => dispatch(fetchViewForSchema(WORKING_ORDER_ITEMS, schemaId, false)),
    onLoadCustomViews: () => dispatch(fetchCustomGridViews()),
});

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps,
)(ShipmentReviewRootContainer);
